<script lang="ts">
	//Formatters
	import { format } from 'svelte-i18n';
	//Helpers
	import { get_local_ip, real_id } from '../../../helpers/interface_helpers';
	import { onSettingsResponse } from '../../../helpers/device_settings_helpers';
	import {
		getExtSensorsSettings,
		getSensorAutomationOptions,
	} from '../../../helpers/ext_sensor_helpers';
	import { valueOutOfRange } from '../../../helpers/utilities';
	//Services
	import { localReq, postReq } from '../../../services/http';
	//Design system components
	import { Select, Input, Button, Text } from '@Shelly/shelly-design-system';
	//Types
	import type { Device } from '../../../types/Device';

	export let device: Device;

	let waitForResponse = false;

	const id = real_id(device.id),
		ext_humidity_settings = getExtSensorsSettings(device, 'humidity'),
		min = 0,
		max = 100,
		options = getSensorAutomationOptions(),
		setHumAutomation = async (sensor: any) => {
			let res;
			waitForResponse = true;
			const local_ip = get_local_ip(id);
			if (local_ip) {
				const prefix = device.channels_count > 1 ? `relay${device.channel}_` : '';
				res = await localReq(
					device,
					`${local_ip}/settings/ext_humidity/${sensor.extid}`,
					'post',
					{
						[`${prefix}overhum_threshold`]: sensor.overhum_threshold,
						[`${prefix}underhum_threshold`]: sensor.underhum_threshold,
						[`${prefix}overhum_act`]: sensor.overhum_act,
						[`${prefix}underhum_act`]: sensor.underhum_act,
					},
					6000,
				);
			} else {
				res = await postReq(`/device/relay/settings/humidity_actions`, {
					id,
					extid: sensor.extid,
					channel: device.channel,
					overhum_threshold: sensor.overhum_threshold,
					underhum_threshold: sensor.underhum_threshold,
					overhum_act: sensor.overhum_act,
					underhum_act: sensor.underhum_act,
				});
			}
			const toastTexts = {
				success: $format('humidity_automation_scc_toast', {
					default: 'Humidity automation is saved!',
				}),
			};
			onSettingsResponse(res, device, toastTexts);
			waitForResponse = false;
		};
</script>

{#each ext_humidity_settings as sensor}
	<Text class="mt-m mb-s">{sensor.name}</Text>
	<Text styleType="body-small" class="mb-s"
		>{$format('ext_sensor_over_humidity_value', {
			default: 'Over-humidity threshold in %:',
		})}</Text
	>
	<Input
		aria-label={$format('input_over_humidity_threshold', {
			default: 'Input Over-humidity threshold',
		})}
		size="m"
		class="mb-s"
		value={sensor.overhum_threshold}
		icon="fas fa-calculator"
		error={valueOutOfRange(sensor.overhum_threshold, min, max)
			? $format('min_max_error_description', {
					values: { min, max },
					default: 'Value must be between {min} and {max}.',
				})
			: ''}
		on:input={(e) => {
			sensor.overhum_threshold = e.target['value'];
		}}
		id="input-1o4k00r"
	/>

	<Text styleType="body-small" class="mb-s"
		>{$format('ext_sensor_over_humidity_action', {
			default: 'Over-humidity action:',
		})}
	</Text>
	<Select
		aria-label={$format('select_over_humidity_action', {
			default: 'Select Over-humidity action',
		})}
		size="m"
		class="mb-s"
		value={options.find((i) => i.value == sensor.overhum_act)}
		icon="fas fa-humidity"
		{options}
		on:change={(e) => {
			sensor.overhum_act = e.detail['value'];
		}}
		id="select-igpcsg4"
	/>

	<Text styleType="body-small" class="mb-s">
		{$format('ext_sebnbsor_under_humidity_value', {
			default: 'Under-humidity threshold in %:',
		})}
	</Text>
	<Input
		aria-label={$format('input_under_humidity_threshold', {
			default: 'Input Under-humidity threshold',
		})}
		size="m"
		class="mb-s"
		value={sensor.underhum_threshold}
		icon="fas fa-calculator"
		error={valueOutOfRange(sensor.underhum_threshold, min, max)
			? $format('min_max_error_description', {
					values: { min, max },
					default: 'Value must be between {min} and {max}.',
				})
			: ''}
		on:input={(e) => {
			sensor.underhum_threshold = e.target['value'];
		}}
		id="input-bz8hotm"
	/>

	<Text styleType="body-small" class="mb-s">
		{$format('ext_sensor_under_humidity_action', {
			default: 'Under-humidity action:',
		})}
	</Text>
	<Select
		aria-label={$format('select_under_humidity_action', {
			default: 'Select Under-humidity action',
		})}
		size="m"
		class="mb-s"
		value={options.find((i) => i.value == sensor.underhum_act)}
		icon="fas fa-hand-holding-water"
		{options}
		on:change={(e) => {
			sensor.underhum_act = e.detail['value'];
		}}
		id="select-kfovfoy"
	/>
	<Button
		size="m"
		width="full"
		loading={waitForResponse}
		disabled={valueOutOfRange(sensor.overhum_threshold, min, max) ||
			valueOutOfRange(sensor.underhum_threshold, min, max)}
		on:click={() => setHumAutomation(sensor)}>{$format('button_save', { default: 'Save' })}</Button
	>
{/each}
